import { hasScope } from '@mesa/core/auth/utils';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ErrorState } from '@/components/error-state';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { WebhookCreateDialog } from '@/components/webhook-create-dialog';
import { WebhookEditDialog } from '@/components/webhook-edit-dialog';
import { pushError } from '@/lib/faro';
import { orgBySlugQueryOptions } from '@/lib/org-query-options';
import { webhookListQueryOptions } from '@/lib/webhook-query-options';
import { deleteWebhookTargetServerFn } from '@/server/functions/webhook';

const relativeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

function formatRelativeTime(date: Date | string | null): string {
  if (!date) return 'Never';
  const d = date instanceof Date ? date : new Date(date);
  const diff = Date.now() - d.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return relativeFormatter.format(-minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return relativeFormatter.format(-hours, 'hour');
  const days = Math.floor(hours / 24);
  if (days < 30) return relativeFormatter.format(-days, 'day');
  const months = Math.floor(days / 30);
  if (months < 12) return relativeFormatter.format(-months, 'month');
  return relativeFormatter.format(-Math.floor(months / 12), 'year');
}

function WebhooksSkeleton() {
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Icon name="webhook" />
          Webhook Targets
        </h1>
        <Skeleton className="h-9 w-36 rounded" />
      </div>
      <div className="border border-mesa-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Target</TableHead>
              <TableHead>Listening to</TableHead>
              <TableHead>Last triggered</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-48 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16 rounded" />
                </TableCell>
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/$orgSlug/webhooks')({
  ssr: 'data-only',
  loader: async ({ context: { queryClient, org } }) => {
    await queryClient.ensureQueryData(webhookListQueryOptions(org.slug));
  },
  pendingComponent: WebhooksSkeleton,
  errorComponent: ErrorState,
  component: WebhooksPage,
});

function WebhooksPage() {
  const { org: routeOrg } = Route.useRouteContext();
  const { orgSlug } = Route.useParams();
  const { data: queryOrg } = useSuspenseQuery(orgBySlugQueryOptions(orgSlug));
  const org = queryOrg ?? routeOrg;
  const canManage = hasScope(org.scopes, 'admin');
  const queryClient = useQueryClient();

  const { data: webhooks } = useSuspenseQuery(webhookListQueryOptions(org.slug));

  const [pendingDelete, setPendingDelete] = useState<{ id: string; url: string } | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<(typeof webhooks)[number] | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (webhookTargetId: string) =>
      deleteWebhookTargetServerFn({ data: { orgSlug: org.slug, webhookTargetId } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['webhook-list', org.slug] });
      setPendingDelete(null);
    },
    onError: (err) => {
      pushError(err);
    },
  });

  if (webhooks.length === 0) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Icon name="webhook" />
            Webhook Targets
          </h1>
          {canManage && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <Icon name="plus" className="h-4 w-4" />
              Create Webhook Target
            </Button>
          )}
        </div>
        <div className="border border-mesa-border">
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="webhook" className="h-12 w-12 text-mesa-muted mb-4" />
            <h3 className="text-lg font-semibold mb-2">No targets yet</h3>
            <p className="text-mesa-muted text-center mb-4">Create one to start receiving event notifications.</p>
            {canManage && (
              <Button onClick={() => setIsCreateOpen(true)}>
                <Icon name="plus" className="h-4 w-4" />
                Create Webhook Target
              </Button>
            )}
          </div>
        </div>
        <WebhookCreateDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} orgSlug={org.slug} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Icon name="webhook" />
          Webhook Targets
        </h1>
        {canManage && (
          <Button onClick={() => setIsCreateOpen(true)}>
            <Icon name="plus" className="h-4 w-4" />
            Create Webhook Target
          </Button>
        )}
      </div>

      <div className="border border-mesa-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Target</TableHead>
              <TableHead>Listening to</TableHead>
              <TableHead>Last triggered</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {webhooks.map((webhook) => (
              <TableRow key={webhook.id} className="group/row">
                <TableCell>
                  <Link
                    to="/$orgSlug/webhooks/$id"
                    params={{ orgSlug: org.slug, id: webhook.id }}
                    preload="intent"
                    className="block hover:underline"
                  >
                    {webhook.name ? (
                      <>
                        <div className="font-medium">{webhook.name}</div>
                        <div className="text-xs text-mesa-muted truncate max-w-md">{webhook.url}</div>
                      </>
                    ) : (
                      <div className="font-medium truncate max-w-md">{webhook.url}</div>
                    )}
                  </Link>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-mesa-muted">
                    {webhook.events.length} {webhook.events.length === 1 ? 'event' : 'events'}
                  </span>
                </TableCell>
                <TableCell>
                  {webhook.lastTriggeredAt ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-sm text-mesa-muted cursor-default">
                          {formatRelativeTime(webhook.lastTriggeredAt)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{new Date(webhook.lastTriggeredAt).toLocaleString()}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-sm text-mesa-muted">Never</span>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm text-mesa-muted cursor-default">
                        {formatRelativeTime(webhook.createdAt)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{new Date(webhook.createdAt).toLocaleString()}</TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {canManage && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Icon name="ellipsis" className="h-4 w-4" />
                          <span className="sr-only">Actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditingWebhook(webhook)}>
                          <Icon name="pen" className="h-4 w-4 mr-2" />
                          Edit target
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-mesa-red focus:text-mesa-red"
                          onClick={() => setPendingDelete({ id: webhook.id, url: webhook.url })}
                        >
                          <Icon name="trash" className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-sm text-mesa-muted">
        Showing {webhooks.length} of {webhooks.length} targets
      </p>

      <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this target?</AlertDialogTitle>
            <AlertDialogDescription>
              The target at <code className="font-mono text-xs">{pendingDelete?.url}</code> will stop receiving events
              immediately. Delivery history will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
              className="bg-mesa-red text-white hover:bg-mesa-red/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete target'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WebhookCreateDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} orgSlug={org.slug} />

      {editingWebhook && (
        <WebhookEditDialog
          open={editingWebhook !== null}
          onOpenChange={(next) => !next && setEditingWebhook(null)}
          orgSlug={org.slug}
          webhook={editingWebhook}
        />
      )}
    </div>
  );
}
